import { useState, useEffect } from 'react';
import { Columns2, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction
} from '@/components/ui/alert-dialog';
import { PERSONA_STATUS_CONFIG } from '../constants';
import { PersonaSummaryContent } from './persona-summary-content';
import { PersonaValidationFlow } from './persona-validation-flow';
import { PersonaDownloadMenu } from './persona-download-menu';
import { PersonaRegenerateModal } from './persona-regenerate-modal';
import { PersonaRegeneratingOverlay } from './persona-regenerating-overlay';
import { PersonaValidatedBanner } from './persona-validated-banner';
import { PersonaRestoreNotice } from './persona-restore-notice';
import type { Persona } from '@/types';

interface PersonaDetailSheetProps {
	persona: Persona;
	name: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function PersonaDetailSheet({ persona, name, open, onOpenChange }: PersonaDetailSheetProps) {
	const [hasProgress, setHasProgress] = useState(false);
	const [showCancelGuard, setShowCancelGuard] = useState(false);
	const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
	const [justValidated, setJustValidated] = useState(false);
	const [regenerationLabel, setRegenerationLabel] = useState<'Regenerating Persona' | 'Updating Persona'>(
		'Regenerating Persona'
	);

	const isGenerating = persona.generationStatus === 'generating';

	useEffect(() => {
		if (!isGenerating) setRegenerationLabel('Regenerating Persona');
		// A new round starts: the "validated" banner belonged to the validation before it.
		else setJustValidated(false);
	}, [isGenerating]);

	const isValidated = persona.validationStatus === 'validated';
	const statusConfig = PERSONA_STATUS_CONFIG[persona.validationStatus];

	const requestClose = () => {
		// Only an open validation has progress to lose; after a restore the persona is validated
		// and the flow's last progress report is stale.
		if (hasProgress && !isGenerating && !isValidated) {
			setShowCancelGuard(true);
			return;
		}
		onOpenChange(false);
	};

	return (
		<>
			<Sheet open={open} onOpenChange={(next) => (next ? onOpenChange(next) : requestClose())}>
				<SheetContent
					side='right'
					showClose={false}
					className='w-full sm:max-w-[680px]'
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<SheetHeader className='flex-row items-start justify-between border-b border-gray-500 bg-white'>
						<div>
							<SheetTitle>{name}</SheetTitle>
							<p className='text-xs text-muted-foreground whitespace-nowrap'>
								{persona.userId == null ? 'Applies to all agents' : 'Applies to this agent'}
							</p>
						</div>
						<div className='flex items-center gap-2'>
							{isGenerating ? (
								<span className='flex items-center gap-1.5 rounded-2xl bg-gray-100 pl-2.5 pr-2.5 py-1 text-xs text-gray-800'>
									<Loader2 className='size-3 animate-spin' />
									{regenerationLabel}
								</span>
							) : (
								<Badge variant={statusConfig.badgeVariant}>{statusConfig.label}</Badge>
							)}
							{!isGenerating && (
								<>
									{isValidated && (
										<Button
											variant='outline'
											className='rounded-full gap-1.5 font-normal border-gray-500 shadow-none bg-white'
											onClick={() => setRegenerateModalOpen(true)}
										>
											<RefreshCw className='size-3.5' />
											Regenerate
										</Button>
									)}
									<Button
										variant='outline'
										className='rounded-full gap-1.5 font-normal border-gray-500 shadow-none bg-white'
										asChild
									>
										<a href={`/persona/${persona.id}/guide`} target='_blank' rel='noopener noreferrer'>
											<ExternalLink className='size-3.5' />
											Detailed Persona
										</a>
									</Button>
									<PersonaDownloadMenu persona={persona} name={name} />
								</>
							)}
							<Button variant='ghost' size='icon-sm' className='rounded-full' onClick={requestClose}>
								<Columns2 className='size-3.5' />
							</Button>
						</div>
					</SheetHeader>

					<div className='flex-1 overflow-y-auto p-4 flex flex-col gap-4'>
						{/* A newer version waits for validation and an older one was validated: offer to go back. */}
						{!isGenerating && !isValidated && persona.hasValidatedVersion && (
							<PersonaRestoreNotice personaId={persona.id} />
						)}
						{isGenerating ? (
							<PersonaRegeneratingOverlay label={regenerationLabel} />
						) : isValidated ? (
							<PersonaSummaryContent personaId={persona.id} />
						) : (
							<PersonaValidationFlow
								persona={persona}
								label={name}
								onProgressChange={setHasProgress}
								onValidated={() => setJustValidated(true)}
								onRegenerationStart={(kind) =>
									setRegenerationLabel(kind === 'update' ? 'Updating Persona' : 'Regenerating Persona')
								}
							/>
						)}

						{isValidated && !isGenerating && <PersonaValidatedBanner show={justValidated} />}
					</div>
				</SheetContent>
			</Sheet>

			<PersonaRegenerateModal
				personaId={persona.id}
				label={name}
				open={regenerateModalOpen}
				onOpenChange={setRegenerateModalOpen}
			/>

<AlertDialog open={showCancelGuard} onOpenChange={setShowCancelGuard}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancel Validation</AlertDialogTitle>
						<AlertDialogDescription>Are you sure you want to leave the validation Process.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Stay</AlertDialogCancel>
						<AlertDialogAction
							className='bg-red-500 hover:bg-red-600 text-white border-none'
							onClick={() => {
								setShowCancelGuard(false);
								onOpenChange(false);
							}}
						>
							Yes, Cancel
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
